import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../AuthContext';
import Calendar from '../components/Calendar';
import PipelineBoard from '../components/PipelineBoard';
import { formatBidNumber } from '../utils/bidNumber';
import { parseWallClock } from '../utils/datetime';
import '../styles/home.css';
import '../styles/notifications.css';

const STATUS_CLASS = {
  'Bidding': 'status-bidding',
  'Pending': 'status-pending',
  'Budget': 'status-budget',
  'On Hold': 'status-on-hold',
  'Pipeline': 'status-pipeline',
  'Awarded': 'status-awarded',
  'Not Awarded': 'status-not-awarded',
};

const STATUS_DISPLAY = { 'Pipeline': 'Opportunities' };
function displayStatus(s) { return STATUS_DISPLAY[s] || s; }

function formatCurrency(val) {
  if (!val || val === 0) return '$0';
  if (val >= 1000000) return `$${(val / 1000000).toFixed(1)}M`;
  if (val >= 1000) return `$${(val / 1000).toFixed(0)}K`;
  return `$${val.toFixed(0)}`;
}

function isMyProject(project, displayName) {
  if (!displayName) return true;
  return project.Estimator === displayName
    || project.BidCoordinator === displayName
    || project.ProjectExecutive === displayName
    || project.ProjectManager === displayName;
}

export default function HomePage() {
  const [allProjects, setAllProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('dashboard');
  const [scope, setScope] = useState('mine');
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    async function load() {
      try {
        const data = await api.getProjects();
        setAllProjects(data);
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    }
    load();
  }, []);

  const projects = useMemo(() => {
    if (scope === 'company' || !user?.displayName) return allProjects;
    return allProjects.filter(p => isMyProject(p, user.displayName));
  }, [allProjects, scope, user]);

  const stats = useMemo(() => {
    const byStatus = {};
    projects.forEach(p => {
      byStatus[p.StatusName] = (byStatus[p.StatusName] || []);
      byStatus[p.StatusName].push(p);
    });

    const bidding = byStatus['Bidding'] || [];
    const pending = byStatus['Pending'] || [];
    const awarded = byStatus['Awarded'] || [];
    const notAwarded = byStatus['Not Awarded'] || [];
    const pipeline = byStatus['Pipeline'] || [];
    const budget = byStatus['Budget'] || [];
    const onHold = byStatus['On Hold'] || [];

    const projectValue = p => p.ProjectCost || p.EstActualContractValue || 0;
    const totalValue = projects.reduce((s, p) => s + projectValue(p), 0);
    const activePipelineValue = [...bidding, ...budget, ...pending]
      .reduce((s, p) => s + projectValue(p), 0);
    const biddingPendingValue = [...bidding, ...pending]
      .reduce((s, p) => s + projectValue(p), 0);
    const totalAwardedValue = awarded.reduce((s, p) => s + projectValue(p), 0);
    const totalDecided = awarded.length + notAwarded.length;
    const winRate = totalDecided > 0 ? ((awarded.length / totalDecided) * 100).toFixed(0) : 0;
    const winPoolValue = [...awarded, ...notAwarded].reduce((s, p) => s + projectValue(p), 0);
    const potentialRevenue = biddingPendingValue * (winRate / 100);
    const hitPool = awarded.length + notAwarded.length + pending.length;
    const hitRate = hitPool > 0 ? ((awarded.length / hitPool) * 100).toFixed(0) : 0;
    const hitPoolValue = [...awarded, ...notAwarded, ...pending].reduce((s, p) => s + projectValue(p), 0);
    const avgBidSize = projects.length > 0 ? projects.reduce((s, p) => s + projectValue(p), 0) / projects.length : 0;

    // Upcoming deadlines (next 14 days)
    const now = new Date();
    const twoWeeks = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000);
    const upcoming = projects
      .filter(p => {
        const deadline = p.GCOwnerDeadline || p.InternalDeadline || p.SubDueDate;
        if (!deadline) return false;
        const d = parseWallClock(deadline);
        return d >= now && d <= twoWeeks && p.StatusName !== 'Awarded' && p.StatusName !== 'Not Awarded';
      })
      .sort((a, b) => {
        const da = parseWallClock(a.GCOwnerDeadline || a.InternalDeadline || a.SubDueDate);
        const db = parseWallClock(b.GCOwnerDeadline || b.InternalDeadline || b.SubDueDate);
        return da - db;
      });

    // Overdue
    const overdue = projects.filter(p => {
      const deadline = p.GCOwnerDeadline || p.InternalDeadline || p.SubDueDate;
      if (!deadline) return false;
      return parseWallClock(deadline) < now && p.StatusName !== 'Awarded' && p.StatusName !== 'Not Awarded';
    });

    // By estimator — track total + per-status breakdown for the stacked bar
    const byEstimator = {};
    projects.filter(p => p.Estimator && p.StatusName !== 'Awarded' && p.StatusName !== 'Not Awarded').forEach(p => {
      if (!byEstimator[p.Estimator]) byEstimator[p.Estimator] = { total: 0, byStatus: {} };
      byEstimator[p.Estimator].total += 1;
      byEstimator[p.Estimator].byStatus[p.StatusName] = (byEstimator[p.Estimator].byStatus[p.StatusName] || 0) + 1;
    });
    const maxEstimatorTotal = Math.max(1, ...Object.values(byEstimator).map(d => d.total));

    // By client (top 10 by project count)
    const byClient = {};
    projects.forEach(p => {
      if (p.ClientName) {
        byClient[p.ClientName] = (byClient[p.ClientName] || 0) + 1;
      }
    });
    const topClients = Object.entries(byClient).sort((a, b) => b[1] - a[1]).slice(0, 8);

    // Win rate by estimator
    const estimatorWinLoss = {};
    projects.filter(p => p.Estimator && (p.StatusName === 'Awarded' || p.StatusName === 'Not Awarded')).forEach(p => {
      if (!estimatorWinLoss[p.Estimator]) estimatorWinLoss[p.Estimator] = { wins: 0, losses: 0 };
      if (p.StatusName === 'Awarded') estimatorWinLoss[p.Estimator].wins++;
      else estimatorWinLoss[p.Estimator].losses++;
    });

    return {
      byStatus, bidding, pending, awarded, notAwarded, pipeline, budget, onHold,
      totalValue, activePipelineValue, potentialRevenue, totalAwardedValue, winRate, winPoolValue, hitRate, hitPoolValue, avgBidSize,
      upcoming, overdue, byEstimator, maxEstimatorTotal, topClients, estimatorWinLoss,
    };
  }, [projects]);

  if (loading) return <div className="loading">Loading BidBoard...</div>;

  return (
    <>
      <div className="top-bar">
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <h2>BidBoard</h2>
          <div className="scope-toggle">
            <button className={`scope-btn ${scope === 'mine' ? 'active' : ''}`} onClick={() => setScope('mine')}>
              My Dashboard
            </button>
            <button className={`scope-btn ${scope === 'company' ? 'active' : ''}`} onClick={() => setScope('company')}>
              Company
            </button>
          </div>
          {scope === 'mine' && user?.displayName && (
            <span className="scope-user">{user.displayName}</span>
          )}
        </div>
        <div className="view-tabs">
          <button className={`tab ${activeView === 'dashboard' ? 'active' : ''}`} onClick={() => setActiveView('dashboard')}>Dashboard</button>
          <button className={`tab ${activeView === 'calendar' ? 'active' : ''}`} onClick={() => setActiveView('calendar')}>Calendar</button>
          <button className={`tab ${activeView === 'pipeline' ? 'active' : ''}`} onClick={() => setActiveView('pipeline')}>Board</button>
        </div>
      </div>

      <div className="page-content">
        {activeView === 'dashboard' && (
          <DashboardView stats={stats} projects={projects} navigate={navigate} />
        )}
        {activeView === 'calendar' && (
          <Calendar projects={projects} navigate={navigate} />
        )}
        {activeView === 'pipeline' && (
          <PipelineBoard projects={projects} navigate={navigate} />
        )}
      </div>
    </>
  );
}

function NotificationsCard({ navigate }) {
  const [notifications, setNotifications] = useState([]);
  const { user, getHeaders } = useAuth();

  function fetchNotifs() {
    fetch('/api/notifications/unread', { headers: getHeaders(), credentials: 'same-origin' })
      .then(r => r.ok ? r.json() : [])
      .then(setNotifications)
      .catch(() => {});
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchNotifs();
    const interval = setInterval(fetchNotifs, 60000);
    return () => clearInterval(interval);
  }, [user?.displayName]); // eslint-disable-line react-hooks/exhaustive-deps

  function dismiss(id) {
    fetch(`/api/notifications/${id}/read`, { method: 'PATCH', headers: getHeaders(), credentials: 'same-origin' })
      .then(() => setNotifications(prev => prev.filter(n => n.NotificationId !== id)))
      .catch(() => {});
  }

  function handleClick(n) {
    if (n.ProjectId) navigate(`/projects/${n.ProjectId}`);
  }

  return (
    <div className="card dashboard-card">
      <div className="card-header">
        <h3>Notifications</h3>
        {notifications.length > 0 && <span className="badge-count">{notifications.length}</span>}
      </div>
      <div className="card-body" style={{ padding: 0 }}>
        <div style={{ height: '350px', overflowY: 'auto' }}>
          {notifications.length === 0 ? (
            <p style={{ fontSize: '13px', color: '#999', padding: '24px 16px', textAlign: 'center' }}>No new notifications</p>
          ) : notifications.map(n => {
            const clickable = !!n.ProjectId;
            return (
              <div
                key={n.NotificationId}
                className={`notif-card-item ${clickable ? 'notif-clickable' : ''}`}
                onClick={() => clickable && handleClick(n)}
              >
                <div className="notif-card-content">
                  <div className="notif-card-message">{n.Message.replace(/^\[\d{4}-\d{2}\]\s*/, '')}</div>
                  <div className="notif-card-time">{new Date(n.CreatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                </div>
                <button className="notif-card-dismiss" onClick={e => { e.stopPropagation(); dismiss(n.NotificationId); }} title="Dismiss">&times;</button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function RemindersCard({ navigate }) {
  const [followups, setFollowups] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getUpcomingFollowups()
      .then(data => { setFollowups(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  async function handleComplete(id) {
    try {
      await api.completeFollowup(id);
      setFollowups(prev => prev.filter(f => f.FollowUpId !== id));
    } catch (err) {
      console.error(err);
    }
  }

  function daysUntilDue(dateStr) {
    const d = new Date(dateStr);
    const now = new Date();
    return Math.ceil((d - now) / (1000 * 60 * 60 * 24));
  }

  return (
    <div className="card dashboard-card">
      <div className="card-header">
        <h3>Reminders</h3>
        <span className="badge-count">{followups.length}</span>
      </div>
      <div className="card-body" style={{ padding: 0 }}>
        {loading ? (
          <p style={{ padding: '20px', fontSize: '13px', color: '#777' }}>Loading...</p>
        ) : followups.length === 0 ? (
          <p style={{ padding: '20px', fontSize: '13px', color: '#777', textAlign: 'center' }}>No upcoming reminders</p>
        ) : followups.map(f => {
          const days = daysUntilDue(f.DueDate);
          const isOverdue = days < 0;
          const isToday = days === 0;
          const isUrgent = days <= 2 && days >= 0;
          return (
            <div key={f.FollowUpId} style={{
              display: 'flex', alignItems: 'flex-start', gap: '10px',
              padding: '10px 16px', borderBottom: '1px solid var(--color-gray-100)',
              borderLeft: isOverdue ? '3px solid #dc2626' : isUrgent ? '3px solid #f59e0b' : '3px solid transparent',
            }}>
              <button onClick={() => handleComplete(f.FollowUpId)} style={{
                width: '18px', height: '18px', borderRadius: '50%', border: '2px solid #ccc',
                background: 'none', cursor: 'pointer', flexShrink: 0, marginTop: '2px',
              }} title="Mark complete" />
              <div style={{ flex: 1, minWidth: 0 }} onClick={() => navigate(`/projects/${f.ProjectId}`)} role="button" tabIndex={0}>
                <div style={{ fontSize: '13px', fontWeight: 500 }}>{f.Note}</div>
                <div style={{ fontSize: '11px', color: '#777', marginTop: '2px' }}>
                  {f.ProjectName || f.BidNumber} &middot;{' '}
                  <span style={{ color: isOverdue ? '#dc2626' : isToday ? '#f59e0b' : undefined, fontWeight: isOverdue || isToday ? 600 : 400 }}>
                    {isOverdue ? `${Math.abs(days)}d overdue` : isToday ? 'Due today' : `Due in ${days}d`}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DashboardView({ stats, projects, navigate }) {
  return (
    <>
      {/* KPI Row */}
      <div className="kpi-row">
        <div className="kpi-card" title="Uses Bid Amount if entered, otherwise Projected Bid Amount">
          <div className="kpi-icon kpi-blue">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
          </div>
          <div className="kpi-content">
            <div className="kpi-value">{formatCurrency(stats.totalValue)}</div>
            <div className="kpi-label">Total Value</div>
          </div>
        </div>
        <div className="kpi-card" title="Bidding + Budget + Pending — Bid Amount if entered, otherwise Projected">
          <div className="kpi-icon kpi-green">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
          </div>
          <div className="kpi-content">
            <div className="kpi-value">{formatCurrency(stats.activePipelineValue)}</div>
            <div className="kpi-label">Active Pipeline</div>
          </div>
        </div>
        <div className="kpi-card" title="(Bidding + Pending value) × Win Rate %">
          <div className="kpi-icon kpi-emerald">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
          </div>
          <div className="kpi-content">
            <div className="kpi-value">{formatCurrency(stats.potentialRevenue)}</div>
            <div className="kpi-label">Potential Revenue</div>
          </div>
        </div>
        <div className="kpi-card" title="Awarded / (Awarded + Not Awarded)">
          <div className="kpi-icon kpi-purple">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 3v18h18"/><polyline points="7 14 11 10 15 14 21 8"/></svg>
          </div>
          <div className="kpi-content">
            <div className="kpi-value">{stats.winRate}%</div>
            <div className="kpi-label">Win Rate</div>
            <div className="kpi-sub">{formatCurrency(stats.totalAwardedValue)} of {formatCurrency(stats.winPoolValue)}</div>
          </div>
        </div>
        <div className="kpi-card" title="Awarded / (Awarded + Not Awarded + Pending)">
          <div className="kpi-icon kpi-indigo">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
          </div>
          <div className="kpi-content">
            <div className="kpi-value">{stats.hitRate}%</div>
            <div className="kpi-label">Hit Rate</div>
            <div className="kpi-sub">{formatCurrency(stats.totalAwardedValue)} of {formatCurrency(stats.hitPoolValue)}</div>
          </div>
        </div>
        <div className="kpi-card" title="All projects across every status">
          <div className="kpi-icon kpi-amber">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
          </div>
          <div className="kpi-content">
            <div className="kpi-value">{projects.length}</div>
            <div className="kpi-label">Total Projects</div>
          </div>
        </div>
        <div className="kpi-card" title="Total project value / Total projects">
          <div className="kpi-icon kpi-cyan">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2v20M2 12h20"/></svg>
          </div>
          <div className="kpi-content">
            <div className="kpi-value">{formatCurrency(stats.avgBidSize)}</div>
            <div className="kpi-label">Avg Bid Size</div>
          </div>
        </div>
      </div>

      {/* Status breakdown */}
      <div className="status-summary-row">
        {['Pipeline', 'Budget', 'Bidding', 'Pending', 'On Hold', 'Awarded', 'Not Awarded'].map(status => {
          const items = stats.byStatus[status] || [];
          const useBidAmount = ['Pending', 'Awarded', 'Not Awarded'].includes(status);
          const val = items.reduce((s, p) => s + (useBidAmount ? (p.ProjectCost || p.EstActualContractValue || 0) : (p.EstActualContractValue || p.ProjectCost || 0)), 0);
          const STATUS_TIPS = {
            'Pipeline': 'Early-stage opportunities — uses Projected Bid Amount',
            'Budget': 'ROM / pre-financing estimates — uses Projected Bid Amount',
            'Bidding': 'Actively estimating — uses Projected Bid Amount',
            'Pending': 'Proposal submitted, awaiting response — uses Bid Amount',
            'On Hold': 'Project paused — uses Projected Bid Amount',
            'Awarded': 'We won the project — uses Bid Amount',
            'Not Awarded': 'Awarded to another contractor — uses Bid Amount',
          };
          return (
            <div key={status} className="status-summary-card" title={STATUS_TIPS[status] || ''} onClick={() => navigate(`/?status=${encodeURIComponent(status)}`)}>
              <div className="status-summary-count">{items.length}</div>
              <div className={`status-badge ${STATUS_CLASS[status]}`}>{displayStatus(status)}</div>
              <div className="status-summary-value">{formatCurrency(val)}</div>
            </div>
          );
        })}
      </div>

      {/* Weekly Calendar — today + 7 days */}
      <WeekStrip projects={projects} navigate={navigate} />

      <div className="dashboard-grid">
        {/* Upcoming Deadlines */}
        <div className="card dashboard-card">
          <div className="card-header">
            <h3>Upcoming Deadlines</h3>
            <span className="badge-count">{stats.upcoming.length}</span>
          </div>
          <div className="card-body deadline-list">
            {stats.overdue.length > 0 && (
              <div className="overdue-banner">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                {stats.overdue.length} overdue bid{stats.overdue.length > 1 ? 's' : ''}
              </div>
            )}
            {stats.upcoming.length === 0 ? (
              <p className="empty-text">No deadlines in the next 14 days</p>
            ) : stats.upcoming.slice(0, 8).map(p => {
              const deadline = parseWallClock(p.GCOwnerDeadline || p.InternalDeadline || p.SubDueDate);
              const now = new Date();
              const daysLeft = Math.ceil((deadline - now) / (1000 * 60 * 60 * 24));
              const urgency = daysLeft <= 2 ? 'urgent' : daysLeft <= 5 ? 'warning' : 'normal';
              return (
                <div key={p.ProjectId} className={`deadline-item ${urgency} ${p.IsPriority ? 'cm-priority-row' : ''}`} onClick={() => navigate(`/projects/${p.ProjectId}`)}>
                  <div className="deadline-info">
                    <div className="deadline-project">{p.ProjectName}</div>
                    <div className="deadline-meta">
                      {p.IsPriority && <img src="/favicon.png" alt="" style={{ width: '12px', height: '12px', verticalAlign: '-1px', marginRight: '3px', opacity: 0.8 }} />}
                      <span className="deadline-bid">{formatBidNumber(p.BidNumber, p.RevisionNumber)}</span>
                      <span className="deadline-estimator">{p.Estimator || 'Unassigned'}</span>
                    </div>
                  </div>
                  <div className="deadline-countdown">
                    <div className={`countdown-badge ${urgency}`}>
                      {daysLeft === 0 ? 'TODAY' : daysLeft === 1 ? 'TOMORROW' : `${daysLeft}d`}
                    </div>
                    <div className="deadline-date">{deadline.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Notifications */}
        <NotificationsCard navigate={navigate} />

        {/* Reminders */}
        <RemindersCard navigate={navigate} />
      </div>
    </>
  );
}

const DEADLINE_FIELDS = [
  { key: 'SubDueDate', label: 'Sub Bids Due', color: '#dc2626' },
  { key: 'GCOwnerDeadline', label: 'Owner Deadline', color: '#d97706' },
  { key: 'InternalDeadline', label: 'Bid Due to Mgr', color: '#7c3aed' },
  { key: 'BidWalkDate', label: 'Bid Walk', color: '#0284c7' },
  { key: 'EstBidStart', label: 'Est. Bid Comm.', color: '#059669' },
  { key: 'DateBidSubmitted', label: 'Submitted', color: '#475569' },
];

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function WeekStrip({ projects, navigate }) {
  const days = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const result = [];
    for (let i = 0; i < 8; i++) {
      const d = new Date(today);
      d.setDate(d.getDate() + i);
      result.push({ date: d, items: [] });
    }

    const activeProjects = projects.filter(p =>
      p.StatusName !== 'Awarded' && p.StatusName !== 'Not Awarded'
    );

    activeProjects.forEach(p => {
      DEADLINE_FIELDS.forEach(f => {
        if (!p[f.key]) return;
        const deadlineDate = parseWallClock(p[f.key]);
        deadlineDate.setHours(0, 0, 0, 0);
        const dayIdx = result.findIndex(r => r.date.getTime() === deadlineDate.getTime());
        if (dayIdx >= 0) {
          result[dayIdx].items.push({
            projectId: p.ProjectId,
            bidNumber: p.BidNumber,
            revisionNumber: p.RevisionNumber,
            projectName: p.ProjectName,
            type: f.label,
            color: f.color,
            time: p[f.key],
            isPriority: p.IsPriority,
          });
        }
      });
    });

    return result;
  }, [projects]);

  const totalItems = days.reduce((s, d) => s + d.items.length, 0);
  if (totalItems === 0) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return (
    <div className="card" style={{ marginBottom: '20px' }}>
      <div className="card-header">
        <h3>This Week</h3>
        <span className="badge-count">{totalItems}</span>
      </div>
      <div className="card-body" style={{ padding: 0 }}>
        <div className="week-strip">
          {days.map((day, i) => {
            const isToday = day.date.getTime() === today.getTime();
            const dayLabel = isToday ? 'Today' : WEEKDAYS[day.date.getDay()];
            const dateLabel = day.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            return (
              <div key={i} className={`week-strip-day ${isToday ? 'week-strip-today' : ''} ${day.items.length === 0 ? 'week-strip-empty' : ''}`}>
                <div className="week-strip-header">
                  <div className="week-strip-dayname">{dayLabel}</div>
                  <div className="week-strip-date">{dateLabel}</div>
                </div>
                <div className="week-strip-items">
                  {day.items.map((item, j) => (
                    <div
                      key={j}
                      className={`week-strip-item ${item.isPriority ? 'cm-priority-row' : ''}`}
                      onClick={() => navigate(`/projects/${item.projectId}`)}
                      style={{ borderLeftColor: item.color }}
                    >
                      <div className="week-strip-item-type" style={{ color: item.color }}>{item.type}</div>
                      <div className="week-strip-item-project">{item.projectName}</div>
                      <div className="week-strip-item-bid">{formatBidNumber(item.bidNumber, item.revisionNumber)}</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
